import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { RESPONSIVE, SPACING, SKELETONS } from "@/design-system";

interface ExpertProfileSkeletonProps {
  className?: string;
}

/**
 * ExpertProfileSkeleton - Matches the exact layout of ExpertProfile.tsx
 * 
 * Structure:
 * - Back button
 * - 3-column grid (lg:col-span-2 + sidebar)
 * - Profile card: avatar + name + badges
 * - Bio section
 * - Sidebar: location card + publications list
 */
export function ExpertProfileSkeleton({ className }: ExpertProfileSkeletonProps) {
  return (
    <main className={cn("container", RESPONSIVE.pageContainerPy, className)}>
      {/* Back button skeleton */}
      <Skeleton className={cn(RESPONSIVE.marginBottomResponsive, SKELETONS.elements.button.default)} />
      {/* Responsive grid layout matching actual component */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 ${RESPONSIVE.gapResponsiveLg}`}>
        {/* Main content - 2 columns on large screens */}
        <div className={`lg:col-span-2 ${RESPONSIVE.spaceYResponsiveLarge}`}>
          <Card>
            <CardHeader className={RESPONSIVE.paddingCardContentWide}>
              {/* Profile header - stacks on mobile */}
              <div className={RESPONSIVE.profileHeaderLayout}>
                <Skeleton className={cn(SKELETONS.elements.avatar.xl, "sm:h-24 sm:w-24 flex-shrink-0")} />
                <div className={cn(SPACING.listItemGap, 'flex-1 w-full', RESPONSIVE.textAlignCenterToLeft)}>
                  <Skeleton className={cn(SKELETONS.elements.title.xl, 'mx-auto sm:mx-0')} />
                  <Skeleton className={cn(SKELETONS.elements.subtitle.lg, 'mx-auto sm:mx-0')} />
                  <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap pt-2">
                    <Skeleton className={SKELETONS.elements.badge.default} />
                    <Skeleton className={SKELETONS.elements.badge.lg} />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className={`${RESPONSIVE.paddingCardContentWide} pt-0 space-y-3`}>
              <Skeleton className={SKELETONS.elements.content.line} />
              <Skeleton className={SKELETONS.elements.content.line} />
              <Skeleton className={SKELETONS.elements.content.lineShort} />
            </CardContent>
          </Card>
          
          {/* Publications section skeleton */}
          <Card>
            <CardHeader className={RESPONSIVE.paddingCardContentWide}>
              <Skeleton className={cn(SKELETONS.elements.title.default, 'sm:h-6')} />
            </CardHeader>
            <CardContent className={`${RESPONSIVE.paddingCardContentWide} pt-0 space-y-3`}>
              {[1, 2].map(i => (
                <div key={i} className={`flex ${RESPONSIVE.gapResponsive}`}>
                  <Skeleton className={cn(SKELETONS.elements.image.thumbnail, 'sm:h-20 sm:w-20 flex-shrink-0')} />
                  <div className={cn(SPACING.listItemGap, 'flex-1')}>
                    <Skeleton className={cn(SKELETONS.elements.title.default, 'sm:h-5 w-full')} />
                    <Skeleton className={cn(SKELETONS.elements.subtitle.default, 'w-3/4')} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
        
        {/* Sidebar - hidden on mobile, shown on lg */}
        <div className={cn(RESPONSIVE.desktopOnly, SPACING.pageContainer)}>
          <Card>
            <CardHeader>
              <Skeleton className={SKELETONS.elements.title.sm} />
            </CardHeader>
            <CardContent className={cn(SPACING.cardContent, SPACING.stackLg)}>
              <Skeleton className={SKELETONS.elements.button.lg} />
              <Skeleton className={SKELETONS.elements.content.line} />
              <Skeleton className={SKELETONS.elements.content.lineShort} />
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
